import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  MessageSquare, Plus, Search, Users
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import ChatPage from '../../components/Chat';

const MessagesPage = () => {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [initialMessage, setInitialMessage] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('movemate_user') || '{}');
    setCurrentUser(user);
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/users/list');
      const customerList = (response.data || []).filter(u => u.role === 'customer');
      setCustomers(customerList);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const handleOpenNewChat = () => {
    fetchCustomers();
    setShowNewChat(true);
  };

  const handleStartConversation = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/conversations', {
        customer_id: selectedCustomer.id,
        initial_message: initialMessage || undefined
      });
      
      if (response.data.existing) {
        toast.info('Opened existing conversation');
      } else {
        toast.success('Conversation started!');
      }
      
      setShowNewChat(false);
      setSelectedCustomer(null);
      setInitialMessage('');
      
      // Refresh page to show new conversation
      window.location.reload();
    } catch (error) {
      toast.error('Failed to start conversation');
    } finally {
      setCreating(false);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    const email = (c.email || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">{t('messages.title', 'Messages')}</h1>
          <p className="text-gray-600">{t('messages.subtitle', 'Chat with your customers')}</p>
        </div>
        <Button onClick={handleOpenNewChat} data-testid="new-chat-btn">
          <Plus size={16} className="mr-2" />
          {t('messages.new_conversation', 'New Conversation')}
        </Button>
      </div>

      <ChatPage currentUser={currentUser} />

      {/* New Conversation Dialog */}
      <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="text-primary" size={20} />
              Start New Conversation
            </DialogTitle>
            <DialogDescription>
              Select a customer to start messaging
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="max-h-48 overflow-y-auto border rounded-lg">
              {filteredCustomers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <Users size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No customers found</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => setSelectedCustomer(customer)}
                      className={`p-3 cursor-pointer hover:bg-gray-50 ${
                        selectedCustomer?.id === customer.id ? 'bg-primary/10 border-l-4 border-primary' : ''
                      }`}
                    >
                      <p className="font-medium">
                        {customer.first_name} {customer.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{customer.email || customer.phone}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedCustomer && (
              <div className="p-3 bg-primary/5 rounded-lg">
                <p className="text-sm text-gray-600">Selected:</p>
                <p className="font-semibold">
                  {selectedCustomer.first_name} {selectedCustomer.last_name}
                </p>
              </div>
            )}

            <div>
              <Label>Initial Message (Optional)</Label>
              <Textarea
                placeholder="Type your first message..."
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewChat(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleStartConversation} 
              disabled={!selectedCustomer || creating}
            >
              {creating ? 'Starting...' : 'Start Conversation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesPage;
